import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Upload } from "lucide-react";

const KEYS = ["home.hero.title_1", "home.hero.title_2", "home.hero.lede", "home.hero.image"];
const LABELS = {
  "home.hero.title_1": "Titre — ligne 1",
  "home.hero.title_2": "Titre — ligne 2 (italique dorée)",
  "home.hero.lede": "Sous-titre / paragraphe d'accroche",
  "home.hero.image": "Photo hero (URL ou upload)",
};

function ImageUploaderInline({ onUploaded, testid }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data.url);
      toast.success("Photo importée");
    } catch {
      toast.error("Import échoué");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" onChange={handle} className="hidden" data-testid={`${testid}-input`} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
        className="border border-[#0A1128]/30 text-[#0A1128] px-4 py-2 text-xs tracking-[0.2em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center gap-2 disabled:opacity-60"
        data-testid={`${testid}-button`}>
        <Upload size={14} /> {busy ? "..." : "Importer"}
      </button>
    </>
  );
}

export default function HomeEditor({ onSitePublicRefresh }) {
  const [blocks, setBlocks] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/site-content");
      const map = {};
      (r.data.blocks || []).forEach((b) => { map[b.key] = { fr: b.fr, en: b.en, type: b.type }; });
      setBlocks(map);
      setDirty({});
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const setField = (key, lang, val) => {
    setBlocks((b) => ({ ...b, [key]: { ...(b[key] || {}), [lang]: val } }));
    setDirty((d) => ({ ...d, [key]: true }));
  };

  const save = async (key) => {
    setSaving(key);
    try {
      const b = blocks[key] || {};
      await api.put(`/admin/content/${key}`, { fr: b.fr || "", en: b.en || "" });
      toast.success("Enregistré");
      setDirty((d) => ({ ...d, [key]: false }));
      if (onSitePublicRefresh) onSitePublicRefresh();
    } catch {
      toast.error("Échec");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-3xl text-[#0A1128]">Page d&apos;accueil</h2>
        <p className="text-sm text-[#0A1128]/60 mt-1">Texte d&apos;accroche et photo du hero visible sur la page d&apos;accueil.</p>
      </div>
      {KEYS.map((key) => {
        const isImage = key === "home.hero.image";
        const b = blocks[key] || {};
        return (
          <div key={key} className="bg-white border border-[#0A1128]/10 p-6" data-testid={`home-${key}`}>
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="text-sm font-medium text-[#0A1128]">{LABELS[key]}</div>
                <code className="text-xs text-[#0A1128]/40">{key}</code>
              </div>
              {dirty[key] && (
                <button onClick={() => save(key)} disabled={saving === key} className="bg-[#0A1128] text-[#F9F8F6] px-5 py-2 text-xs tracking-[0.24em] uppercase flex items-center gap-2 disabled:opacity-60" data-testid={`home-save-${key}`}>
                  <Save size={14} /> {saving === key ? "..." : "Enregistrer"}
                </button>
              )}
            </div>
            {isImage ? (
              <div className="space-y-3">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="overline text-[#0A1128]/60">URL / chemin (FR)</label>
                    <div className="flex gap-2 mt-1">
                      <input value={b.fr || ""} onChange={(e) => setField(key, "fr", e.target.value)} className="flex-1 border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid="home-image-fr" />
                      <ImageUploaderInline onUploaded={(url) => setField(key, "fr", url)} testid="home-hero-upload-fr" />
                    </div>
                    {b.fr && <img src={b.fr} alt="" className="mt-3 w-full h-40 object-cover" />}
                  </div>
                  <div>
                    <label className="overline text-[#0A1128]/60">URL / chemin (EN)</label>
                    <div className="flex gap-2 mt-1">
                      <input value={b.en || ""} onChange={(e) => setField(key, "en", e.target.value)} className="flex-1 border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid="home-image-en" />
                      <ImageUploaderInline onUploaded={(url) => setField(key, "en", url)} testid="home-hero-upload-en" />
                    </div>
                    {b.en && <img src={b.en} alt="" className="mt-3 w-full h-40 object-cover" />}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="overline text-[#0A1128]/60">FR</label>
                  {key === "home.hero.lede" ? (
                    <textarea rows={4} value={b.fr || ""} onChange={(e) => setField(key, "fr", e.target.value)} className="w-full border border-[#0A1128]/15 px-3 py-2 text-sm mt-1 resize-none" data-testid={`home-${key}-fr`} />
                  ) : (
                    <input value={b.fr || ""} onChange={(e) => setField(key, "fr", e.target.value)} className="w-full border-b border-[#0A1128]/20 py-2 focus:outline-none focus:border-[#D4AF37] mt-1" data-testid={`home-${key}-fr`} />
                  )}
                </div>
                <div>
                  <label className="overline text-[#0A1128]/60">EN</label>
                  {key === "home.hero.lede" ? (
                    <textarea rows={4} value={b.en || ""} onChange={(e) => setField(key, "en", e.target.value)} className="w-full border border-[#0A1128]/15 px-3 py-2 text-sm mt-1 resize-none" />
                  ) : (
                    <input value={b.en || ""} onChange={(e) => setField(key, "en", e.target.value)} className="w-full border-b border-[#0A1128]/20 py-2 focus:outline-none focus:border-[#D4AF37] mt-1" />
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
